<template>
  <div class="dashboard-page mt-3">
    <CCard class="dashboard-card border-0 shadow-sm mt-3">
      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show>
          {{ errorMessage }}
        </CAlert>

        <div class="filter-bar mb-3">
          <DateRangePicker
            v-model="filters"
            :disabled="isLoading"
            @preset="applyFilters"
          />
          <div class="filter-actions">
            <CButton
              color="primary"
              class="action-btn"
              :disabled="isLoading"
              @click="applyFilters"
            >
              {{ isLoading ? 'Loading...' : 'Search' }}
            </CButton>
            <CButton
              color="dark"
              variant="outline"
              class="action-btn reload-icon-btn"
              :disabled="isLoading"
              title="Reload"
              aria-label="Reload"
              @click="reloadData"
            >
              <span class="reload-glyph" aria-hidden="true">&#x21bb;</span>
            </CButton>
          </div>
        </div>

        <CRow class="tight-row tiles-row pt-3">
          <!-- Initial-load skeletons. Once tiles arrive the real cards
               take over (refresh-fetches keep showing the previous tiles
               instead of flashing the skeletons). -->
          <template v-if="isLoading && !tileCards.length">
            <CCol
              v-for="i in skeletonTileCount"
              :key="`sk-${i}`"
              xl="3"
              lg="4"
              sm="6"
              class="mb-2"
              style="height: 95px !important"
            >
              <div class="tile-skeleton" aria-hidden="true">
                <div class="tile-skeleton__body">
                  <div class="tile-skeleton__lines">
                    <div class="tile-skeleton__line tile-skeleton__line--value"></div>
                    <div class="tile-skeleton__line tile-skeleton__line--label"></div>
                  </div>
                </div>
              </div>
            </CCol>
          </template>
          <CCol
            v-for="(card, index) in tileCards"
            :key="card.key"
            xl="3"
            lg="4"
            sm="6"
            class="mb-2"
            style="height: 95px !important"
          >
            <CCard
              :class="['h-100 border-0 tile-card', tileCardClass(card.key, index), { 'is-clickable': tileTab(card.key) }]"
              :role="tileTab(card.key) ? 'button' : null"
              :tabindex="tileTab(card.key) ? 0 : null"
              @click.native="onTileClick(card.key)"
              @keydown.native.enter="onTileClick(card.key)"
              @keydown.native.space.prevent="onTileClick(card.key)"
            >
              <button
                v-if="tileInfo(card.key)"
                type="button"
                class="tile-info-btn"
                :aria-label="`What is ${card.label}?`"
                @click.stop
                @keydown.enter.stop
                @keydown.space.stop.prevent
              >
                <span class="tile-info-glyph" aria-hidden="true">i</span>
                <span class="tile-info-tip" role="tooltip">{{ tileInfo(card.key) }}</span>
              </button>
              <CCardBody class="tile-body">
                <div class="tile-text">
                  <div class="tile-value">{{ formatTileValue(card.key, card.value) }}</div>
                  <div class="tile-label">{{ card.label }}</div>
                </div>
                <span class="tile-icon" aria-hidden="true">
                  <CIcon :name="tileIcon(card.key)" size="lg" />
                </span>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <div ref="reportsSection" class="reports-section pt-3">
          <div class="report-tabs-wrap">
            <button
              v-show="canScrollLeft"
              type="button"
              class="report-tabs-arrow report-tabs-arrow--left"
              aria-label="Scroll tabs left"
              @click="scrollTabs(-1)"
            >
              <span aria-hidden="true">&#x2039;</span>
            </button>
            <div
              ref="reportTabs"
              class="report-tabs"
              role="tablist"
              @scroll="updateTabScrollState"
            >
              <button
                v-for="tab in reportTabs"
                :key="tab.key"
                type="button"
                role="tab"
                class="report-tab"
                :class="{ 'is-active': tab.key === activeReport }"
                :aria-selected="tab.key === activeReport"
                @click="activeReport = tab.key"
              >
                <span class="tab-label">{{ tab.label }}</span>
                <span v-if="!isLoading && tab.items.length" class="tab-count">{{ tab.items.length }}</span>
              </button>
            </div>
            <button
              v-show="canScrollRight"
              type="button"
              class="report-tabs-arrow report-tabs-arrow--right"
              aria-label="Scroll tabs right"
              @click="scrollTabs(1)"
            >
              <span aria-hidden="true">&#x203a;</span>
            </button>
          </div>

          <div class="report-filters">
            <div class="rf-field rf-field--search">
              <label class="rf-label">Search</label>
              <AppInput
                no-margin
                :value="tableSearch"
                placeholder="Search this report…"
                @update:value="tableSearch = $event"
              />
            </div>
            <div
              v-for="def in activeFilterDefs"
              :key="def.key"
              class="rf-field"
            >
              <label class="rf-label">{{ def.label }}</label>
              <AppDropdown
                v-model="activeFilters[def.key]"
                :options="filterOptions(def.key)"
              />
            </div>
            <div class="rf-field rf-field--action">
              <label class="rf-label" aria-hidden="true">&nbsp;</label>
              <button
                type="button"
                class="rf-reset"
                :disabled="!hasActiveTableFilters"
                @click="clearTableFilters"
              >
                Clear
              </button>
            </div>
          </div>

          <BaseTable
            :accent="activeTab.accent"
            :items="filteredItems"
            :columns="activeTab.columns || null"
            :loading="isLoading"
            :empty-text="filteredEmptyText"
            :show-count="false"
          >
            <template #cell-status="{ value }">
              <span :class="['status-pill', statusPillClass(value)]">{{ statusLabel(value) }}</span>
            </template>
            <template #cell-channel="{ value }">
              <span :class="['channel-pill', channelPillClass(value)]">{{ channelLabel(value) }}</span>
            </template>
            <template #cell-customer="{ row }">
              <div class="customer-cell">
                <span class="customer-name">{{ row.customer || '—' }}</span>
                <span v-if="row.phone" class="customer-phone">{{ row.phone }}</span>
              </div>
            </template>
          </BaseTable>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from 'axios'
import AppInput from '@/components/AppInput.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import { tileIcon as tileIconFor } from '@/config/dashboardTileIcons'

const TILE_LABELS = {
  no_of_orders_today: 'My Orders',
  my_co_orders: 'My C/O Orders',
  my_online_orders: 'My Online Orders',
  no_of_calls_today: 'My Calls',
  conversion_rate: 'My Conversion Rate',
  bottles_sold_today: 'Bottles Sold by Me',
  farm_bottles_sold: 'Farm Bottles Sold by Me',
  kgs_sold: 'Kgs Sold by Me',
  co_kgs_got: 'C/O Kgs Got',
  co_farm_bottles_got: 'C/O Farm Bottles Got',
  co_bottles_got: 'C/O Bottles Got',
  return_rate: 'My Return Rate',
  returns: 'Total Returns',
  returned_bottles: 'Total Bottles Returned',
  returned_kgs: 'Total Kgs Returned',
  hms_patients_attributed: 'My HMS Patients'
}

const TILE_ORDER = [
  'bottles_sold_today',
  'co_bottles_got',
  'farm_bottles_sold',
  'co_farm_bottles_got',
  'kgs_sold',
  'co_kgs_got',
  'no_of_orders_today',
  'my_co_orders',
  'my_online_orders',
  'no_of_calls_today',
  'conversion_rate',
  'return_rate',
  'returns',
  'returned_bottles',
  'returned_kgs',
  'hms_patients_attributed'
]

// Tile keys the server still returns but we deliberately don't render.
const HIDDEN_TILES = ['sales_amount', 'pending_payment', 'my_bulk_orders']

// Optional "i" tooltip copy per tile key — only tiles that need explaining.
// Each entry is keyed by language code; the staff's department decides which
// language is shown (server returns `language`: ml / ta / en).
const TILE_INFO = {
  farm_bottles_sold: {
    en: 'Bottles of the farm range you sold — products whose name ends with "farm". These are counted separately and are not included in Bottles Sold by Me, so the two tiles together give your full bottle total.'
  },
  co_kgs_got: {
    en: 'Kgs of Agro Yield Super (1kg) on C/O orders handed off to you (care-of you). Counts only that product; every other item on those C/O orders is in C/O Bottles Got instead.'
  },
  co_farm_bottles_got: {
    en: 'Bottles of the farm range on C/O orders handed off to you (care-of you) — products whose name ends with "farm". Counted separately from C/O Bottles Got, so the two tiles together give your full C/O bottle total.'
  },
  co_bottles_got: {
    en: 'Bottles on C/O orders handed off to you (care-of you). For Manakkal Agro this excludes Agro Yield Super (1kg), counted as kgs in C/O Kgs Got; for Manakkal Veterinary Pharma it excludes the farm range, counted in C/O Farm Bottles Got — so the split tiles together give your full C/O quantity total.'
  },
  my_co_orders: {
    en: 'C/O (Care-Of) orders are direct orders — taken over the phone or at the counter — that are placed on a customer\'s behalf and attributed to you. Orders from online marketplaces (Amazon, Flipkart, Meesho, Website) are not counted here; they appear under My Online Orders.',
    ml: 'C/O (Care-Of) ഓർഡറുകൾ എന്നാൽ ഫോണിലൂടെയോ കൗണ്ടറിലൂടെയോ നേരിട്ട് എടുക്കുന്ന, ഒരു ഉപഭോക്താവിന് വേണ്ടി താങ്കളുടെ പേരിൽ രേഖപ്പെടുത്തുന്ന ഓർഡറുകളാണ്. ഓൺലൈൻ മാർക്കറ്റ്‌പ്ലേസുകളിൽ നിന്നുള്ള (Amazon, Flipkart, Meesho, Website) ഓർഡറുകൾ ഇവിടെ ഉൾപ്പെടുത്തിയിട്ടില്ല; അവ "My Online Orders"-ൽ കാണാം.',
    ta: 'C/O (Care-Of) ஆர்டர்கள் என்பது தொலைபேசி மூலமாகவோ அல்லது கவுன்டரிலோ நேரடியாக எடுக்கப்பட்டு, ஒரு வாடிக்கையாளர் சார்பாக உங்கள் பெயரில் பதிவு செய்யப்படும் ஆர்டர்கள் ஆகும். ஆன்லைன் சந்தைகளில் (Amazon, Flipkart, Meesho, Website) இருந்து வரும் ஆர்டர்கள் இங்கே சேர்க்கப்படவில்லை; அவை "My Online Orders"-ல் காணப்படும்.'
  },
  my_online_orders: {
    en: 'Online orders are orders from online marketplaces (Amazon, Flipkart, Meesho, Website) that are attributed to you. Direct phone or counter orders are not counted here; they appear under My C/O Orders.',
    ml: 'Online ഓർഡറുകൾ എന്നാൽ ഓൺലൈൻ മാർക്കറ്റ്‌പ്ലേസുകളിൽ നിന്നുള്ള (Amazon, Flipkart, Meesho, Website) താങ്കളുടെ പേരിൽ രേഖപ്പെടുത്തിയ ഓർഡറുകളാണ്. ഫോണിലൂടെയോ കൗണ്ടറിലൂടെയോ നേരിട്ട് എടുക്കുന്ന ഓർഡറുകൾ ഇവിടെ ഉൾപ്പെടുത്തിയിട്ടില്ല; അവ "My C/O Orders"-ൽ കാണാം.',
    ta: 'Online ஆர்டர்கள் என்பது ஆன்லைன் சந்தைகளில் (Amazon, Flipkart, Meesho, Website) இருந்து வந்து, உங்கள் பெயரில் பதிவு செய்யப்படும் ஆர்டர்கள் ஆகும். தொலைபேசி அல்லது கவுன்டரில் நேரடியாக எடுக்கப்படும் ஆர்டர்கள் இங்கே சேர்க்கப்படவில்லை; அவை "My C/O Orders"-ல் காணப்படும்.'
  }
}

// Tiles that deep-link to a report tab when clicked (tile key → tab key).
const TILE_TO_TAB = {
  no_of_orders_today: 'orders',
  my_co_orders: 'co_orders',
  my_online_orders: 'online',
  returns: 'returns',
  hms_patients_attributed: 'hms_patients'
}

export default {
  name: 'TelesalesDashboard',
  components: { AppInput, AppDropdown },
  data () {
    const today = this.toDateInputValue(new Date())
    return {
      isLoading: false,
      errorMessage: '',
      lastUpdatedAt: null,
      filters: {
        fromDate: today,
        toDate: today
      },
      tiles: {},
      // UI language for tile tooltips, set from the dashboard response based
      // on the staff member's department (ml / ta / en).
      language: 'en',
      dailyBreakdown: [],
      itemwiseReport: [],
      recentOrders: [],
      myCoOrders: [],
      myOnlineOrders: [],
      myReturns: [],
      myHmsPatients: [],
      hmsEnabled: false,
      activeReport: 'daily',
      // Per-tab client-side report filtering. tableSearch is a free-text
      // match across row values; activeFilters holds the selected value for
      // each of the active tab's dropdown filters (rebuilt on tab change).
      tableSearch: '',
      activeFilters: {},
      // Overflow-arrow state for the horizontally-scrollable report tab strip.
      canScrollLeft: false,
      canScrollRight: false
    }
  },
  computed: {
    reportTabs () {
      const tabs = [
        {
          key: 'daily',
          label: 'My Daily Performance',
          accent: 'var(--brand-gradient)',
          items: this.dailyBreakdown,
          emptyText: 'No activity recorded for the selected date range.'
        },
        {
          key: 'item',
          label: 'My Itemwise Sales',
          accent: 'linear-gradient(180deg, #0ea5e9, #0369a1)',
          items: this.itemwiseReport,
          emptyText: 'No items sold in the selected date range.'
        },
        {
          key: 'orders',
          label: 'My Recent Orders',
          accent: 'linear-gradient(180deg, #2a2f8f, #3949ab)',
          items: this.recentOrders,
          // Phone shown under the customer name via the cell-customer slot,
          // so it isn't rendered as its own column.
          columns: [
            { key: '#', label: '#', sortable: false },
            { key: 'order_number', label: 'Order Number' },
            { key: 'customer', label: 'Customer' },
            { key: 'bottles', label: 'Bottles', align: 'right' },
            { key: 'amount', label: 'Amount', format: 'currency', align: 'right' },
            { key: 'payment_status', label: 'Payment Status' },
            { key: 'shipping_status', label: 'Shipping Status' },
            { key: 'order_status', label: 'Order Status' },
            { key: 'created_at', label: 'Created At', format: 'datetime' }
          ],
          filters: [
            { key: 'payment_status', label: 'Payment Status' },
            { key: 'shipping_status', label: 'Shipping Status' },
            { key: 'order_status', label: 'Order Status' }
          ],
          emptyText: 'No orders placed in the selected date range.'
        },
        {
          key: 'co_orders',
          label: 'My C/O Orders',
          accent: 'linear-gradient(180deg, #2f80c8, #4ea3e0)',
          items: this.myCoOrders,
          // Explicit columns so `phone` isn't rendered as its own column —
          // it's shown under the customer name via the cell-customer slot.
          columns: [
            { key: '#', label: '#', sortable: false },
            { key: 'order_number', label: 'Order Number' },
            { key: 'customer', label: 'Customer' },
            { key: 'bottles', label: 'Bottles', align: 'right' },
            { key: 'amount', label: 'Amount', format: 'currency', align: 'right' },
            { key: 'payment_status', label: 'Payment Status' },
            { key: 'shipping_status', label: 'Shipping Status' },
            { key: 'order_status', label: 'Order Status' }
          ],
          filters: [
            { key: 'payment_status', label: 'Payment Status' },
            { key: 'shipping_status', label: 'Shipping Status' },
            { key: 'order_status', label: 'Order Status' }
          ],
          emptyText: 'No C/O orders assigned to you in the selected date range.'
        },
        {
          key: 'online',
          label: 'My Online Orders',
          accent: 'linear-gradient(180deg, #e58e1a, #f1ab3c)',
          items: this.myOnlineOrders,
          // Phone shown under the customer name via the cell-customer slot,
          // so it isn't rendered as its own column. Channel pill identifies
          // the marketplace/website source of each order.
          columns: [
            { key: '#', label: '#', sortable: false },
            { key: 'order_number', label: 'Order Number' },
            { key: 'channel', label: 'Channel' },
            { key: 'customer', label: 'Customer' },
            { key: 'bottles', label: 'Bottles', align: 'right' },
            { key: 'amount', label: 'Amount', format: 'currency', align: 'right' },
            { key: 'created_at', label: 'Order Date', formatter: this.formatOrderDate }
          ],
          filters: [
            { key: 'channel', label: 'Channel' }
          ],
          emptyText: 'No online orders assigned to you in the selected date range.'
        },
        {
          key: 'returns',
          label: 'Total Returns',
          accent: 'linear-gradient(180deg, #c0392b, #e15543)',
          items: this.myReturns,
          // Phone shown under the customer name via the cell-customer slot,
          // so it isn't rendered as its own column.
          columns: [
            { key: '#', label: '#', sortable: false },
            { key: 'order_number', label: 'Order Number' },
            { key: 'customer', label: 'Customer' },
            { key: 'bottles', label: 'Bottles', align: 'right' },
            { key: 'amount', label: 'Amount', format: 'currency', align: 'right' },
            { key: 'payment_status', label: 'Payment Status' },
            { key: 'order_status', label: 'Order Status' },
            { key: 'return_date', label: 'Return Date', format: 'datetime' }
          ],
          filters: [
            { key: 'payment_status', label: 'Payment Status' },
            { key: 'order_status', label: 'Order Status' }
          ],
          emptyText: 'No returns attributed to you in the selected date range.'
        }
      ]
      if (this.hmsEnabled) {
        tabs.push({
          key: 'hms_patients',
          label: 'My HMS Patients',
          accent: 'linear-gradient(180deg, #14b8a6, #0f766e)',
          items: this.myHmsPatients,
          // Explicit columns so the server-supplied `match_type` field isn't
          // rendered as its own column.
          columns: [
            { key: '#', label: '#', sortable: false },
            { key: 'op_no', label: 'OP No' },
            { key: 'patient_name', label: 'Patient Name' },
            { key: 'mobile', label: 'Mobile' },
            { key: 'age', label: 'Age', align: 'right' },
            { key: 'gender', label: 'Gender' },
            { key: 'reg_date', label: 'Reg Date', format: 'date' },
            { key: 'status', label: 'Status' },
            { key: 'incentive_amount', label: 'Incentive', format: 'currency', align: 'right' }
          ],
          filters: [
            { key: 'status', label: 'Status' },
            { key: 'gender', label: 'Gender' }
          ],
          emptyText: 'No HMS patients attributed to you in the selected date range.'
        })
      }
      return tabs
    },
    activeTab () {
      return this.reportTabs.find((tab) => tab.key === this.activeReport) || this.reportTabs[0]
    },
    activeFilterDefs () {
      return (this.activeTab && this.activeTab.filters) || []
    },
    hasActiveTableFilters () {
      if (this.tableSearch.trim()) return true
      return Object.keys(this.activeFilters).some((key) => this.activeFilters[key] !== '')
    },
    filteredItems () {
      const rows = Array.isArray(this.activeTab.items) ? this.activeTab.items : []
      const filters = this.activeFilters
      const query = this.tableSearch.trim().toLowerCase()
      const result = rows.filter((row) => {
        // Dropdown filters — exact string match on the row value.
        const passesFilters = Object.keys(filters).every((key) => {
          const selected = filters[key]
          if (selected === '' || selected === null || selected === undefined) return true
          return String(row[key]) === String(selected)
        })
        if (!passesFilters) return false
        // Free-text search — match any visible field (skip the serial col).
        if (!query) return true
        return Object.keys(row).some((key) => {
          if (key === '#') return false
          const value = row[key]
          return value !== null && value !== undefined && String(value).toLowerCase().includes(query)
        })
      })
      // Re-number the serial so it stays contiguous after filtering.
      return result.map((row, index) => ({ ...row, '#': index + 1 }))
    },
    filteredEmptyText () {
      if (this.hasActiveTableFilters) return 'No results match your search or filters.'
      return this.activeTab.emptyText
    },
    tileCards () {
      const knownKeys = TILE_ORDER.filter((key) => Object.prototype.hasOwnProperty.call(this.tiles, key))
      const otherKeys = Object.keys(this.tiles).filter(
        (key) => !knownKeys.includes(key) && !HIDDEN_TILES.includes(key)
      )
      return [...knownKeys, ...otherKeys].map((key) => ({
        key,
        label: TILE_LABELS[key] || this.formatLabel(key),
        value: this.tiles[key]
      }))
    },
    skeletonTileCount () {
      return TILE_ORDER.length
    }
  },
  watch: {
    activeReport () {
      this.resetTableFilters()
      this.$nextTick(this.updateTabScrollState)
    },
    reportTabs () {
      // Tab set can change (e.g. HMS tab gated by hmsEnabled) — recheck overflow.
      this.$nextTick(this.updateTabScrollState)
    }
  },
  created () {
    this.resetTableFilters()
    this.fetchDashboardData()
  },
  mounted () {
    this.updateTabScrollState()
    window.addEventListener('resize', this.updateTabScrollState)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateTabScrollState)
  },
  methods: {
    updateTabScrollState () {
      const el = this.$refs.reportTabs
      if (!el) {
        this.canScrollLeft = false
        this.canScrollRight = false
        return
      }
      const maxScroll = el.scrollWidth - el.clientWidth
      // 1px slack so sub-pixel rounding doesn't leave a phantom arrow.
      this.canScrollLeft = el.scrollLeft > 1
      this.canScrollRight = el.scrollLeft < maxScroll - 1
    },
    scrollTabs (direction) {
      const el = this.$refs.reportTabs
      if (!el) return
      // Scroll ~75% of the visible width per click.
      el.scrollBy({ left: direction * el.clientWidth * 0.75, behavior: 'smooth' })
    },
    resetTableFilters () {
      // Clear the search and seed one reactive activeFilters key per the
      // active tab's filter definitions (so v-model bindings stay reactive).
      this.tableSearch = ''
      const next = {}
      this.activeFilterDefs.forEach((def) => {
        next[def.key] = ''
      })
      this.activeFilters = next
    },
    clearTableFilters () {
      this.resetTableFilters()
    },
    filterOptions (key) {
      // Distinct values for a filter, derived from the unfiltered tab data.
      const rows = Array.isArray(this.activeTab.items) ? this.activeTab.items : []
      const seen = new Set()
      const options = [{ value: '', label: 'All' }]
      rows.forEach((row) => {
        const value = row[key]
        if (value === null || value === undefined || value === '') return
        const stringValue = String(value)
        if (seen.has(stringValue)) return
        seen.add(stringValue)
        options.push({ value: stringValue, label: this.optionLabel(key, value) })
      })
      return options
    },
    optionLabel (key, value) {
      if (key === 'channel') return this.channelLabel(value)
      if (key === 'status') return this.statusLabel(value)
      return this.formatLabel(value)
    },
    toDateInputValue (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getPayload () {
      return {
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate
      }
    },
    applyFilters () {
      this.fetchDashboardData()
    },
    reloadData () {
      this.fetchDashboardData()
    },
    tileIcon (key) {
      return tileIconFor(key)
    },
    tileCardClass (key, index) {
      const toneMap = {
        bottles_sold_today: 'tile-slate',
        farm_bottles_sold: 'tile-forest',
        kgs_sold: 'tile-emerald',
        co_kgs_got: 'tile-teal',
        co_farm_bottles_got: 'tile-lime',
        co_bottles_got: 'tile-cyan',
        my_co_orders: 'tile-ocean',
        my_online_orders: 'tile-amber',
        no_of_orders_today: 'tile-indigo',
        no_of_calls_today: 'tile-teal',
        conversion_rate: 'tile-violet',
        return_rate: 'tile-orange',
        returns: 'tile-crimson',
        returned_bottles: 'tile-rose',
        returned_kgs: 'tile-lime',
        hms_patients_attributed: 'tile-cyan',
        my_bulk_orders: 'tile-plum',
        sales_amount: 'tile-forest',
        pending_payment: 'tile-gold'
      }
      const fallbackTones = [
        'tile-indigo', 'tile-ocean', 'tile-emerald', 'tile-amber', 'tile-slate', 'tile-crimson',
        'tile-violet', 'tile-teal', 'tile-rose', 'tile-cyan', 'tile-lime', 'tile-orange'
      ]
      return toneMap[key] || fallbackTones[index % fallbackTones.length]
    },
    tileInfo (key) {
      const entry = TILE_INFO[key]
      if (!entry) return null
      return entry[this.language] || entry.en || null
    },
    tileTab (key) {
      // Tab this tile deep-links to, but only if that tab is actually
      // rendered (e.g. HMS tab is gated by hmsEnabled).
      const tab = TILE_TO_TAB[key]
      if (!tab) return null
      return this.reportTabs.some((t) => t.key === tab) ? tab : null
    },
    onTileClick (key) {
      const tab = this.tileTab(key)
      if (!tab) return
      this.activeReport = tab
      this.$nextTick(() => {
        const section = this.$refs.reportsSection
        if (section && section.scrollIntoView) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    async fetchDashboardData () {
      if (!this.filters.fromDate || !this.filters.toDate) {
        this.errorMessage = 'Please select both From Date and To Date.'
        return
      }
      if (this.filters.fromDate > this.filters.toDate) {
        this.errorMessage = 'From Date must be before or equal to To Date.'
        return
      }

      this.isLoading = true
      this.errorMessage = ''

      try {
        const payload = this.getPayload()
        // Combined endpoint — one authed request instead of two parallel
        // POSTs. Halves Sanctum token lookups and DB connection acquires
        // on every dashboard load.
        const response = await axios.post(`${this.$apiAdress}/api/dashboard/telesales/combined`, payload)
        const combined = this.normalizeObject(response.data)

        // Department-driven tooltip language (ml / ta / en). Falls back to
        // English if the server omits it or sends an unknown code.
        this.language = ['ml', 'ta', 'en'].includes(combined.language) ? combined.language : 'en'
        this.tiles = this.normalizeObject(combined.tiles)
        const data = this.normalizeObject(combined.dashboard)
        this.dailyBreakdown = this.withSerial(data.daily_breakdown)
        this.itemwiseReport = this.withSerial(data.itemwise_report)
        this.recentOrders = this.withSerial(data.recent_orders)
        this.myCoOrders = this.withSerial(data.my_co_orders)
        this.myOnlineOrders = this.withSerial(data.my_online_orders)
        this.myReturns = this.withSerial(data.my_returns)
        // Server omits my_hms_patients (and the hms_patients_attributed tile)
        // for non-OMS orgs — use key presence to gate the tab.
        this.hmsEnabled = Object.prototype.hasOwnProperty.call(data, 'my_hms_patients')
        this.myHmsPatients = this.withSerial(data.my_hms_patients)
        if (!this.hmsEnabled && this.activeReport === 'hms_patients') {
          this.activeReport = 'daily'
        }
        this.lastUpdatedAt = new Date()
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to load dashboard data right now.'
      } finally {
        this.isLoading = false
      }
    },
    withSerial (rows) {
      // Prepend a 1-based "#" series so BaseTable's first auto-derived
      // column is always the row number for every report tab.
      if (!Array.isArray(rows)) return []
      return rows.map((row, index) => ({ '#': index + 1, ...row }))
    },
    normalizeObject (payload) {
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        return payload
      }
      return {}
    },
    formatLabel (key) {
      return String(key || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    },
    formatOrderDate (value) {
      // Raw DB datetime string (e.g. "2026-06-06 10:30:00") → "06 Jun 2026".
      if (!value) return '—'
      const date = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    channelLabel (value) {
      const labels = {
        amazon: 'Amazon',
        flipkart: 'Flipkart',
        meesho: 'Meesho',
        ecommerce: 'Website',
        website: 'Website'
      }
      if (value === null || value === undefined || value === '') return '—'
      return labels[value] || this.formatLabel(value)
    },
    channelPillClass (value) {
      const tones = {
        amazon: 'channel-pill--amazon',
        flipkart: 'channel-pill--flipkart',
        meesho: 'channel-pill--meesho',
        ecommerce: 'channel-pill--website',
        website: 'channel-pill--website'
      }
      return tones[value] || 'channel-pill--gray'
    },
    statusLabel (value) {
      // Surfaces HMS attribution statuses with friendlier wording.
      // ineligible_oms → "OMS Customer" because the patient is already
      // an OMS buyer (not an incentive-eligible new lead).
      const labels = {
        pending: 'Pending',
        eligible: 'Eligible',
        ineligible_oms: 'OMS Customer',
        ineligible_no_call: 'Ineligible (No Call)',
        ineligible_returning: 'Ineligible (Returning)',
        needs_review: 'Needs Review',
        locked: 'Credited'
      }
      if (value === null || value === undefined || value === '') return '—'
      return labels[value] || this.formatLabel(value)
    },
    statusPillClass (value) {
      const tones = {
        pending: 'status-pill--yellow',
        eligible: 'status-pill--green',
        ineligible_oms: 'status-pill--blue',
        ineligible_no_call: 'status-pill--gray',
        ineligible_returning: 'status-pill--gray',
        needs_review: 'status-pill--red',
        locked: 'status-pill--green'
      }
      return tones[value] || 'status-pill--gray'
    },
    formatTileValue (key, value) {
      if (value === null || value === undefined || value === '') return 'N/A'
      if (['sales_amount', 'pending_payment'].includes(key)) {
        const numeric = Number(value)
        if (Number.isNaN(numeric)) return value
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 2
        }).format(numeric)
      }
      if (['conversion_rate', 'return_rate'].includes(key)) {
        if (typeof value === 'string' && value.trim().endsWith('%')) return value
        return `${value} %`
      }
      return value
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  border-radius: 14px;
  background: linear-gradient(180deg, #f4f7fb 0%, #eef3fa 100%);
}

.dashboard-card {
  border-radius: 14px;
}

.filter-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.tight-row {
  margin-left: -6px;
  margin-right: -6px;
  row-gap: 0.4rem;
}

.tight-row > [class*='col-'],
.tight-row > .col {
  padding-left: 6px;
  padding-right: 6px;
}

.reports-section {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.report-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.rf-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 200px;
  min-width: 0;
}

.rf-field--search {
  flex: 1 1 280px;
}

.rf-field--action {
  flex: 0 0 auto;
  min-width: 0;
}

.rf-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  margin-bottom: 0.3rem;
}

.rf-reset {
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.rf-reset:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.rf-reset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.rf-reset:focus { outline: none; }
.rf-reset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.report-tabs-wrap {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
}

.report-tabs {
  display: flex;
  gap: 0.25rem;
  flex-wrap: nowrap;
  padding: 0.25rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.report-tabs::-webkit-scrollbar { display: none; }

.report-tabs-arrow {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #e5e9f2;
  border-radius: 50%;
  background: #ffffff;
  color: #475569;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.report-tabs-arrow span { margin-top: -2px; }

.report-tabs-arrow:hover {
  background: #f1f5f9;
  color: #1f2a44;
  border-color: #cbd5e1;
}

.report-tabs-arrow:focus { outline: none; }
.report-tabs-arrow:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.report-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.report-tab:focus { outline: none; box-shadow: none; }
.report-tab:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25); }

.report-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.report-tab.is-active {
  background: var(--brand-gradient);
  color: #ffffff;
  box-shadow: var(--brand-shadow);
}

.tab-count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: rgba(14, 91, 70, 0.10);
  color: var(--brand-color);
}

.report-tab.is-active .tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.filter-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  height: 40px;
  min-width: 110px;
  padding: 0 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 999px;
}

.reload-icon-btn {
  min-width: 40px;
  width: 40px;
  padding: 0;
  background: #ffffff;
}

.reload-glyph {
  font-size: 1.1rem;
  line-height: 1;
  font-weight: 700;
  color: #475569;
}

.tile-card {
  border-radius: 12px;
  position: relative;
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.10);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tile-card::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0) 55%);
  pointer-events: none;
}

.tile-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18);
}

.tile-card.is-clickable {
  cursor: pointer;
}

/* Lift the card above its neighbours while the info tooltip is open so the
   bubble isn't painted over by tiles in the following row. */
.tile-card:hover,
.tile-card:focus-within {
  z-index: 4;
}

.tile-card.is-clickable:focus { outline: none; }
.tile-card.is-clickable:focus-visible {
  outline: none;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.10), 0 0 0 3px rgba(22, 160, 122, 0.45);
}

.tile-body {
  padding: 1.2rem 1.25rem;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  height: 100%;
}

.tile-text {
  min-width: 0;
  flex: 1 1 auto;
}

.tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: #ffffff;
  flex: 0 0 auto;
}

.tile-icon .c-icon {
  width: 20px;
  height: 20px;
  color: #ffffff;
}

.tile-info-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 3;
  width: 20px;
  height: 20px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  cursor: help;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.tile-info-btn:hover,
.tile-info-btn:focus-visible {
  background: rgba(255, 255, 255, 0.32);
  border-color: rgba(255, 255, 255, 0.9);
}

.tile-info-btn:focus { outline: none; }
.tile-info-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.35);
}

.tile-info-glyph {
  font-size: 0.72rem;
  font-weight: 800;
  font-style: italic;
  font-family: Georgia, 'Times New Roman', serif;
  line-height: 1;
  color: #ffffff;
}

.tile-info-tip {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 5;
  width: 240px;
  max-width: 70vw;
  padding: 0.6rem 0.7rem;
  border-radius: 8px;
  background: #1f2a44;
  color: #f8fafc;
  font-size: 0.72rem;
  font-weight: 500;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: 0.01em;
  text-align: left;
  white-space: normal;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.28);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
  pointer-events: none;
}

.tile-info-tip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  right: 8px;
  border: 6px solid transparent;
  border-bottom-color: #1f2a44;
}

.tile-info-btn:hover .tile-info-tip,
.tile-info-btn:focus-visible .tile-info-tip {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.tile-value {
  font-size: 1.3rem;
  font-weight: 800;
  line-height: 1.1;
  color: #ffffff;
  letter-spacing: -0.01em;
  margin-bottom: 0.35rem;
}

.tile-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.01em;
}

.tile-slate    { background: linear-gradient(135deg, #1f2a44 0%, #34405e 100%); }
.tile-indigo   { background: linear-gradient(135deg, #2a2f8f 0%, #3949ab 100%); }
.tile-ocean    { background: linear-gradient(135deg, #2f80c8 0%, #4ea3e0 100%); }
.tile-emerald  { background: linear-gradient(135deg, #2e9d5f 0%, #43b97a 100%); }
.tile-amber    { background: linear-gradient(135deg, #e58e1a 0%, #f1ab3c 100%); }
.tile-crimson  { background: linear-gradient(135deg, #c0392b 0%, #e15543 100%); }
.tile-violet   { background: linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%); }
.tile-teal     { background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%); }
.tile-rose     { background: linear-gradient(135deg, #be185d 0%, #f472b6 100%); }
.tile-cyan     { background: linear-gradient(135deg, #0e7490 0%, #22d3ee 100%); }
.tile-lime     { background: linear-gradient(135deg, #4d7c0f 0%, #84cc16 100%); }
.tile-orange   { background: linear-gradient(135deg, #c2410c 0%, #f97316 100%); }
.tile-plum     { background: linear-gradient(135deg, #581c87 0%, #9333ea 100%); }
.tile-forest   { background: linear-gradient(135deg, #14532d 0%, #16a34a 100%); }
.tile-gold     { background: linear-gradient(135deg, #a16207 0%, #d4a017 100%); }

.customer-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.customer-name {
  font-weight: 600;
  color: #1f2937;
}
.customer-phone {
  font-size: 0.78rem;
  color: #64748b;
}

.channel-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.62rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid transparent;
}
.channel-pill--amazon   { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }
.channel-pill--flipkart { background: #eff6ff; color: #1e40af; border-color: #bfdbfe; }
.channel-pill--meesho   { background: #fdf2f8; color: #9d174d; border-color: #fbcfe8; }
.channel-pill--website  { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
.channel-pill--gray     { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.62rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid transparent;
}
.status-pill--green  { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
.status-pill--yellow { background: #fef9c3; color: #854d0e; border-color: #fde68a; }
.status-pill--blue   { background: #e0f2fe; color: #075985; border-color: #bae6fd; }
.status-pill--red    { background: #fee2e2; color: #991b1b; border-color: #fecaca; }
.status-pill--gray   { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }
.status-pill--slate  { background: #1f2a44; color: #f8fafc; border-color: #1f2a44; }

.tile-skeleton {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef1f7 0%, #f7f9fc 100%);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
}

.tile-skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 0.72) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  animation: tile-shimmer 1.6s ease-in-out infinite;
}

.tile-skeleton__body {
  padding: 1.2rem 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.55rem;
}

.tile-skeleton__lines {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.tile-skeleton__line {
  height: 14px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.08);
}

.tile-skeleton__line--value { width: 55%; height: 18px; }
.tile-skeleton__line--label { width: 78%; height: 10px; }

.tiles-row > [class*='col-']:nth-child(2)  .tile-skeleton::after { animation-delay: 0.08s; }
.tiles-row > [class*='col-']:nth-child(3)  .tile-skeleton::after { animation-delay: 0.16s; }
.tiles-row > [class*='col-']:nth-child(4)  .tile-skeleton::after { animation-delay: 0.24s; }
.tiles-row > [class*='col-']:nth-child(5)  .tile-skeleton::after { animation-delay: 0.32s; }
.tiles-row > [class*='col-']:nth-child(6)  .tile-skeleton::after { animation-delay: 0.40s; }
.tiles-row > [class*='col-']:nth-child(7)  .tile-skeleton::after { animation-delay: 0.48s; }
.tiles-row > [class*='col-']:nth-child(8)  .tile-skeleton::after { animation-delay: 0.56s; }

@keyframes tile-shimmer {
  0%   { transform: translateX(-100%); }
  60%  { transform: translateX(100%); }
  100% { transform: translateX(100%); }
}

@media (max-width: 767px) {
  .filter-bar { flex-direction: column; align-items: stretch; }
  .filter-actions { width: 100%; }
  .action-btn { flex: 1; }
  .report-tabs {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .report-tabs::-webkit-scrollbar { display: none; }
  .report-tab {
    padding: 0.45rem 0.85rem;
    font-size: 0.8rem;
    flex: 0 0 auto;
  }
  .tab-count { font-size: 0.65rem; padding: 0.1rem 0.4rem; }
}
</style>
